import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import {
    Target,
    ArrowDown,
    Clock,
    CalendarDays,
    Percent,
    CheckCircle2,
    ShieldAlert,
    Bot,
    ChevronDown,
    ArrowRight,
} from 'lucide-react';
import { useState } from 'react';
import { siteConfig } from '@/config/site';

const rules = [
    {
        icon: Target,
        title: 'Profit Targets',
        items: [
            'Regular & Swing: Phase 1 - 10%, Phase 2 - 8%',
            'Fast FX: Single phase - 15%',
            'Targets must be reached to pass evaluation',
        ],
    },
    {
        icon: ArrowDown,
        title: 'Drawdown Limits',
        items: [
            '5% maximum daily drawdown',
            '10% maximum overall drawdown',
            'Calculated based on account balance or equity (whichever is higher)',
        ],
    },
    {
        icon: Clock,
        title: 'Time Limits',
        items: [
            'No time limit on evaluation phases',
            'Unlimited duration to reach profit targets',
            'Trade at your own pace without pressure',
        ],
    },
    {
        icon: CalendarDays,
        title: 'Minimum Trading Days',
        items: [
            '3 trading days minimum per phase',
            'A trading day is any day a trade is opened or closed',
            'No maximum trading days requirement',
        ],
    },
    {
        icon: Percent,
        title: 'Profit Split',
        items: [
            '80% of profits go to the trader',
            'Payouts processed regularly',
            'No hidden fees on profit withdrawals',
        ],
    },
    {
        icon: CheckCircle2,
        title: 'Allowed Strategies',
        items: [
            'All trading styles permitted (scalping, day trading, swing)',
            'Expert Advisors (EAs) are allowed',
            'News trading allowed on Swing plan',
            'Hedging is permitted',
        ],
    },
    {
        icon: ShieldAlert,
        title: 'Prohibited Activities',
        items: [
            'Toxic trading strategies (latency arbitrage, etc.)',
            'Account management by third parties',
            'Copy trading between evaluation accounts',
            'Exploiting platform errors or data feed issues',
        ],
    },
    {
        icon: Bot,
        title: 'EA & Automation Rules',
        items: [
            'Expert Advisors are permitted',
            'Must be your own EA or properly licensed',
            'High-frequency tick scalping is not allowed',
            'EAs must comply with all other trading rules',
        ],
    },
];

const faqs = [
    {
        question: 'What happens if I exceed the daily drawdown limit?',
        answer: 'If your account reaches the 5% daily drawdown limit, all open positions will be closed and the account will be considered failed. You will need to purchase a new challenge to try again.',
    },
    {
        question: 'Can I hold trades over the weekend?',
        answer: 'Weekend holding is allowed on the Swing plan. For Regular and Fast FX plans, all positions must be closed before market close on Friday.',
    },
    {
        question: 'Are there any restrictions on lot sizes?',
        answer: 'There are no specific lot size restrictions. However, your position sizing should be consistent with proper risk management and within the drawdown limits.',
    },
    {
        question: 'Can I trade during high-impact news events?',
        answer: 'News trading is fully allowed on the Swing plan. For Regular and Fast FX plans, standard trading during news events is permitted but strategies that solely exploit news volatility may be reviewed.',
    },
    {
        question: 'What counts as a minimum trading day?',
        answer: 'A trading day is counted when at least one trade is opened or closed during a trading session. The trade does not need to be profitable to count as a trading day.',
    },
    {
        question: 'Can I use the same EA on multiple accounts?',
        answer: 'You may use the same EA on multiple accounts, but copy trading between your own evaluation accounts to pass challenges is not permitted. Each account must be traded independently.',
    },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
    const [open, setOpen] = useState(false);

    return (
        <Card
            className="cursor-pointer rounded-xl border border-border bg-card/80 backdrop-blur-sm transition-colors hover:bg-card/90"
            onClick={() => setOpen(!open)}
        >
            <CardHeader className="flex flex-row items-center justify-between p-6">
                <CardTitle className="text-base font-medium text-foreground pr-4">{question}</CardTitle>
                <ChevronDown
                    className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                />
            </CardHeader>
            {open && (
                <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground">{answer}</p>
                </CardContent>
            )}
        </Card>
    );
}

export default function Rules() {
    return (
        <AppLayout title={`Trading Rules - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="bg-background pt-32 pb-16 lg:pt-40 lg:pb-20">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Rules & Guidelines</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Trading Rules & <span className="text-gradient">Guidelines</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                            Clear, transparent rules designed to help you succeed as a funded trader.
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Rules Grid */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Trading"
                        highlight="Rules"
                        description="Everything you need to know about our trading rules and guidelines."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {rules.map((rule) => {
                            const Icon = rule.icon;
                            return (
                                <motion.div key={rule.title} variants={staggerItem}>
                                    <Card className="h-full rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                                        <CardHeader>
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                    <Icon className="h-5 w-5 text-primary" />
                                                </div>
                                                <CardTitle>{rule.title}</CardTitle>
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <ul className="space-y-2">
                                                {rule.items.map((item) => (
                                                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* FAQ */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Frequently Asked"
                        highlight="Questions"
                        description="Common questions about our trading rules and guidelines."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="max-w-3xl mx-auto space-y-4">
                        {faqs.map((faq) => (
                            <motion.div key={faq.question} variants={staggerItem}>
                                <FAQItem question={faq.question} answer={faq.answer} />
                            </motion.div>
                        ))}
                    </div>
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Ready to Get <span className="text-gradient">Funded</span>?
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                            Now that you know the rules, start your journey to becoming a funded trader.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/funding">
                                <Button size="lg" variant="outline">
                                    View Programs
                                </Button>
                            </Link>
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Start Challenge
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
